/* KANON `ankiety-klienta-2026-09-16`, `szkice/kierunek-a.html` and `kierunek-a-porownanie.html` —
 * the right-hand pane: one occurrence's full report, and the inline comparison that replaces it.
 *
 * ── ONE stylesheet for the two, deliberately ────────────────────────────────────────────────
 * They stand in the same card, share the same head with its icon row, the same question labels and
 * the same media sizes, and only ever one of them is on screen. What actually differs is the
 * comparison's four-track grid, which is four blocks at the bottom. Two files would be two copies
 * of a head differing by a title.
 *
 * The STREAM and the chart strip are `SurveysTab.module.css`, and the way one ANSWER is drawn is
 * `SurveyReading.module.css`, because that one is shared with a surface this file does not own.
 *
 * This replaced `SurveyDialog.module.css` on 16 September 2026: the report and the comparison stopped
 * being modals, so the scrim, the popup box, the `dvh` ceiling and the two popup widths went with
 * them.
 *
 * ── The report's four bands are NOT here any more ───────────────────────────────────────────
 * They went to `features/survey-report/ui/SurveyReport.module.css` on 17 September 2026, when
 * `/raporty` became the second surface that draws a report. Nothing was copied: the band, reading,
 * tile, answer and media classes were used by `SurveyReport` alone, so the split left this file
 * with exactly the pane the report is hosted IN. */

/* The CARD takes the whole of the rest of the row, so its right edge lines up with the chart strip
 * above it. What is capped is the reading MEASURE inside it — owner, 16 September 2026, of a card
 * cut short on the right at 1920. See `.answerHead` and `.answerValue`.
 *
 * A TWO-CLASS selector, because the `gap` is also declared by `DetailTab.module.css`'s `.card`,
 * which this element wears as well: two declarations of one property at equal specificity are
 * settled by chunk order, and chunk order differs between the dev server and the build. The band
 * rhythm is 26px and it has to be 26px in both. */
.pane.pane {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-26px);
  min-width: 0;
}

/* The pane's reserve while the read is open. It is the LAST thing on the tab and the tallest thing
 * on the row, so the reserve covers the WAIT alone and the loaded report takes its own height — the
 * same rule and the same reason as Trendy's volume card. */
.paneReserve {
  min-height: var(--h-survey-pane-wait);
}

/* ── the head, shared by the report and the comparison ──────────────────────── */

.head {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: flex-start;
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-detail-sec);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  color: var(--text);
  letter-spacing: var(--ls-detail-sec);
}

.subtitle {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.actions {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
}

/* GEOMETRY ONLY — the treatment is `shared/ui/iconButton.module.css`. */
.action {
  width: var(--h-detail-tiny-btn);
  height: var(--h-detail-tiny-btn);
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

/* „Oznacz jako przejrzaną" is the one real ACT on this tab, and it wears the SAME treatment as the
 * roster's `+` — `shared/ui/emberButton.module.css`, through `MarkReviewedButton`. So this class is
 * that file's six required geometry properties plus the square's width, and it restates nothing the
 * treatment declares: not the fill, not the ink, not the border, not the lip.
 *
 * It is a step larger than the outlined `.action` beside it, at the 30px pointer floor rather than
 * under it. The icon button reaches that floor with a `::after` hit-extend and the ember primary has
 * none — and the act on a head being the biggest control on it is the same thing the roster says
 * with a 36px `+` beside its 32px ghosts. */
.markAction {
  flex: none;
}

/* „Zamknij porównanie" stands apart from „Zamień strony" beside it: two unlabelled squares touching
 * read as one control, and the misfire is not symmetrical — closing loses a comparison the trainer
 * has to rebuild by hand. */
.closeAction {
  margin-inline-start: var(--space-2);
}

/* ── the comparison ─────────────────────────────────────────────────────────── */

.compareTable {
  width: 100%;
  border-spacing: var(--space-0);
  table-layout: fixed;
}

/* ZMIANA | tydz. A | tydz. B STICK to the top of the tab's own scroller.
 *
 * A comparison is twenty rows on a form with a full measurement, and the change column is read down
 * — twelve rows in, a figure with no dates over it says nothing about which of the two weeks rose.
 * The card's own ground under it, because the rows scroll THROUGH it: a transparent head would
 * print the column titles over whatever measurement happened to be passing.
 *
 * On the cells and not on `thead`: the table is `border-collapse: separate` (it has to be — the row
 * rules are `border-top` on each cell), and a sticky `thead` does nothing there.
 *
 * `z-index` in the same declaration and no higher than it needs: the pane has no other stacking
 * context of its own, and the photographs in the rows below are the thing being passed under. */
.compareHeadCell,
.compareChangeHead {
  position: sticky;
  top: var(--space-0);
  z-index: 1;
  background: var(--bg-card);
}

.compareHeadCell {
  padding: var(--space-0) var(--space-3) var(--space-3);
  text-align: start;
  vertical-align: bottom;
}

.compareHeadOrdinal {
  display: block;
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-normal);
  color: var(--text);
}

/* The date and the outcome under a column's ordinal. It is what tells the trainer WHICH two weeks
 * they are reading, so it is `--text-2` and quieter only in size and weight. */
.compareHeadWhen {
  display: block;
  font-size: var(--fs-eyebrow);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* The change column is SECOND, right after the question — owner, 16 September 2026, „żeby szybciej
 * się czytało". Its width is fixed so the two readings beside it divide what is left equally and a
 * long free-text answer cannot squeeze the one column the trainer is scanning for. */
.compareChangeHead {
  width: var(--w-survey-change-col);

  /* No inline-start padding, so the word stands over the figures rather than ten pixels right of
   * them — the column is read down, and a head that does not line up with its own cells is noise. */
  padding: var(--space-0) var(--space-3) var(--space-3) var(--space-0);
  font-size: var(--fs-tag);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-align: start;
  vertical-align: bottom;
  text-transform: uppercase;
  letter-spacing: var(--ls-lab);
}

/* The question down the side of the comparison. The report's own question style, without the
 * reading measure a table column has no use for. */
.compareName {
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.compareLabel {
  width: var(--w-survey-label);
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-0);
  text-align: start;
  vertical-align: middle;
  border-top: var(--border-hairline) solid var(--border);
}

.compareCell {
  padding: var(--space-3);
  text-align: start;
  vertical-align: middle;
  border-top: var(--border-hairline) solid var(--border);
}

.compareChange {
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-align: start;
  vertical-align: middle;
  white-space: nowrap;
  border-top: var(--border-hairline) solid var(--border);
  font-variant-numeric: tabular-nums;
}

/* A value of the full measurement, indented behind its group exactly as the report indents it, so
 * the ten read as one block in both places. */
.compareSub {
  display: inline-block;
  padding-inline-start: var(--space-14px);
  font-weight: var(--fw-medium);
}

.comparePhotos {
  --survey-photo-w: var(--sz-survey-photo-cmp-w);
  --survey-photo-h: var(--sz-survey-photo-cmp-h);

  max-width: 100%;
}

/* `max-width` on both, because the table is `table-layout: fixed`: at 1280 each reading column is
 * about 176px of usable width and the clip's own 236px hung past the card's right edge with nothing
 * to scroll it back. Fit or scroll, never clip. */
.compareClip {
  --survey-clip-w: var(--sz-survey-clip-cmp-w);
  --survey-clip-h: var(--sz-survey-clip-cmp-h);

  max-width: 100%;
}

/* ── the wait ───────────────────────────────────────────────────────────────── */

.skelPane {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.skelHead {
  width: 100%;
  height: var(--h-survey-head);
}

.skelBlock {
  width: 100%;
  height: var(--h-survey-pane-block);
}
